import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Globe, 
  Sparkles, 
  Check, 
  ChevronRight, 
  ChevronLeft, 
  Trophy, 
  BrainCircuit,
  X,
  Award
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { profileService } from '../services/profileService';
import { dbManager } from '../services/db_manager';

const LANGUAGES = [
  { code: 'pt', name: 'Português', nativeName: 'Português', flag: '🇧🇷' },
  { code: 'en', name: 'English', nativeName: 'Inglês', flag: '🇺🇸' },
  { code: 'es', name: 'Español', nativeName: 'Espanhol', flag: '🇪🇸' },
  { code: 'fr', name: 'Français', nativeName: 'Francês', flag: '🇫🇷' },
  { code: 'de', name: 'Deutsch', nativeName: 'Alemão', flag: '🇩🇪' },
  { code: 'it', name: 'Italiano', nativeName: 'Italiano', flag: '🇮🇹' },
  { code: 'jp', name: '日本語', nativeName: 'Japonês', flag: '🇯🇵' },
  { code: 'cn', name: '中文', nativeName: 'Mandarim', flag: '🇨🇳' },
  { code: 'ru', name: 'Русский', nativeName: 'Russo', flag: '🇷🇺' },
  { code: 'hu', name: 'Magyar', nativeName: 'Húngaro', flag: '🇭🇺' }
];

const CEFR_LEVELS = ['A1', 'A2', 'B1', 'B2', 'C1', 'C2'];

const LEVEL_DESCRIPTIONS: Record<string, string> = {
  'A1': 'Iniciante - Entende frases muito básicas e saudações.',
  'A2': 'Básico - Consegue descrever rotinas, família e assuntos simples.',
  'B1': 'Intermediário - Lida com viagens e expressa opiniões de forma fluida.',
  'B2': 'Intermediário Avançado - Compreende temas complexos e conversa ativamente.',
  'C1': 'Avançado - Fluência natural com uso flexível do idioma.',
  'C2': 'Domínio Pleno - Compreensão espontânea e proficiência nativa.'
};

export default function OnboardingPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, refreshProfile } = useAuth();

  const searchParams = new URLSearchParams(location.search);
  const paramTargetLang = searchParams.get('targetLang') || localStorage.getItem('libritui_onboarding_target_lang') || 'es';

  // 3-step navigation state: 1: Native Language, 2: Proficiency Level, 3: Fluency Journey
  const [step, setStep] = useState<number>(1);
  const [direction, setDirection] = useState<number>(1);

  // Selections
  const [nativeLang, setNativeLang] = useState<string>('pt');
  const [targetLang, setTargetLang] = useState<string>(paramTargetLang);
  const [currentLevel, setCurrentLevel] = useState<string>('A1');

  useEffect(() => {
    if (paramTargetLang) {
      setTargetLang(paramTargetLang);
    }
  }, [paramTargetLang]);

  const handleNativeLangSelect = (code: string) => {
    setNativeLang(code);
    i18n.changeLanguage(code);
  };

  const handleNextStep = () => {
    setDirection(1);
    setStep(prev => Math.min(prev + 1, 3));
  };

  const handlePrevStep = () => {
    setDirection(-1);
    setStep(prev => Math.max(prev - 1, 1));
  };

  const handleFinishOnboarding = async () => {
    const cleanLang = (targetLang || 'es').toLowerCase();
    const cleanLevel = (currentLevel || 'A1').toUpperCase();

    if (user && !user.isGuest) {
      try {
        await profileService.updateProfile(user.id, {
          native_language: nativeLang,
          learning_languages: [cleanLang]
        });

        // Set or update roadmap_progress in Cloudflare D1
        await dbManager.from('roadmap_progress', user.id).upsert({
          user_id: user.id,
          language_code: cleanLang,
          current_level: cleanLevel,
          current_topic_index: 0,
          updated_at: new Date().toISOString()
        });

        await refreshProfile();
      } catch (err) {
        console.warn("Failed to sync onboarding to D1:", err);
      }
    }

    // Direct redirection to the roadmap for the selected language and level
    navigate(`/roadmap/${cleanLang}/${cleanLevel.toLowerCase()}`);
  };

  const slideTransition = {
    enter: (dir: number) => ({
      x: dir > 0 ? 120 : -120,
      opacity: 0
    }),
    center: {
      x: 0,
      opacity: 1
    },
    exit: (dir: number) => ({
      x: dir < 0 ? 120 : -120,
      opacity: 0
    })
  };

  const getTargetLangName = () => {
    const match = LANGUAGES.find(l => l.code === targetLang);
    return match ? match.nativeName : targetLang.toUpperCase();
  };

  return (
    <div className="min-h-screen bg-transparent flex flex-col justify-between py-12 px-6 relative overflow-hidden transition-colors duration-300">
      
      {/* Background patterns */}
      <div className="absolute top-0 left-0 w-full h-full pointer-events-none overflow-hidden z-0">
        <div className="absolute -top-32 -left-32 w-96 h-96 bg-primary/10 rounded-full blur-3xl dark:bg-primary/5"></div>
        <div className="absolute -bottom-32 -right-32 w-96 h-96 bg-ok/10 rounded-full blur-3xl dark:bg-ok/5"></div>
      </div>

      {/* Header & Steps Indicator */}
      <div className="max-w-xl w-full mx-auto z-10 flex flex-col items-center">
        <div className="flex items-center gap-2 mb-2">
          <Sparkles className="text-primary animate-pulse" size={24} />
          <span className="text-2xl font-medium tracking-[-0.03em] text-ink">
            LibriTui
          </span>
        </div>

        {/* 3-Step Indicator */}
        <div className="w-full flex items-center justify-between mt-6 px-4">
          {[1, 2, 3].map((s) => (
            <div key={s} className="flex-1 flex items-center">
              <div 
                className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-xs transition-all duration-300 ${
                  s === step 
                    ? 'bg-ink text-surface scale-110' 
                    : s < step 
                      ? 'bg-ok text-surface' 
                      : 'bg-tint-stone text-muted '
                }`}
              >
                {s < step ? <Check size={14} /> : s}
              </div>
              {s < 3 && (
                <div 
                  className={`flex-1 h-1 mx-2 rounded-full transition-all duration-300 ${
                    s < step ? 'bg-ok' : 'bg-tint-stone '
                  }`}
                />
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Main wizard wrapper */}
      <main className="max-w-2xl w-full mx-auto my-8 bg-surface border border-black/[0.06] dark:border-slate-850 rounded-[28px] p-8 md:p-12 z-10 relative overflow-hidden transition-all duration-300 min-h-[480px] flex flex-col justify-between">
        
        {/* Close Button */}
        <button 
          onClick={() => navigate('/roadmap')}
          className="absolute top-6 right-6 p-2 rounded-full text-muted hover:text-muted hover:bg-tint-stone/60 transition-colors z-20 focus:outline-none"
          aria-label="Fechar"
        >
          <X size={20} />
        </button>

        <AnimatePresence mode="wait" custom={direction}>
          <motion.div
            key={step}
            custom={direction}
            variants={slideTransition}
            initial="enter"
            animate="center"
            exit="exit"
            transition={{ duration: 0.3, ease: "easeInOut" }}
            className="flex-1 flex flex-col justify-between"
          >
            {/* JANELA 1: IDIOMA NATIVO */}
            {step === 1 && (
              <div className="space-y-6">
                <div className="text-center">
                  <h1 className="text-3xl font-medium text-ink leading-tight">
                    Qual seu idioma nativo?
                  </h1>
                  <p className="text-muted font-medium mt-2">
                    Selecione o idioma em que você se comunica nativamente.
                  </p>
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 py-4">
                  {LANGUAGES.map((lang) => {
                    const isSelected = nativeLang === lang.code;
                    return (
                      <button
                        key={lang.code}
                        onClick={() => handleNativeLangSelect(lang.code)}
                        className={`p-4 rounded-2xl border text-center flex flex-col items-center gap-2 transition-all cursor-pointer ${
                          isSelected
                            ? 'border-primary bg-primary/5 dark:bg-primary/10'
                            : 'border-black/[0.06] dark:border-white/[0.06] hover:border-black/[0.06] bg-tint-stone/50 '
                        }`}
                      >
                        <span className="text-3xl">{lang.flag}</span>
                        <span className="font-bold text-sm text-slate-850">
                          {lang.name}
                        </span>
                        <span className="text-xs text-muted font-medium">
                          {lang.nativeName}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* JANELA 2: NÍVEL DE PROFICIÊNCIA */}
            {step === 2 && (
              <div className="space-y-6">
                <div className="text-center">
                  <h1 className="text-3xl font-medium text-ink leading-tight">
                    Qual seu nível de proficiência?
                  </h1>
                  <p className="text-muted font-medium mt-2">
                    Avalie seu conhecimento atual em{' '}
                    <span className="text-primary font-bold">{getTargetLangName()}</span>.
                  </p>
                </div>

                <div className="space-y-6 py-2">
                  {/* Opção A: Seleção manual de nível */}
                  <div className="p-6 rounded-3xl border border-black/[0.06] dark:border-white/[0.06] bg-tint-stone/50 space-y-4">
                    <span className="text-xs font-normal uppercase font-mono tracking-[0.14em] text-muted">
                      Escolha seu nível
                    </span>
                    <div className="grid grid-cols-3 gap-2">
                      {CEFR_LEVELS.map((lvl) => {
                        const isSelected = currentLevel === lvl;
                        return (
                          <button
                            key={lvl}
                            onClick={() => setCurrentLevel(lvl)}
                            className={`py-3 rounded-xl border font-semibold text-sm transition-all ${
                              isSelected
                                ? 'bg-ink text-surface border-primary'
                                : 'border-black/[0.06] dark:border-white/[0.06] hover:bg-tint-stone/60 text-ink '
                            }`}
                          >
                            {lvl}
                          </button>
                        );
                      })}
                    </div>
                    <p className="text-xs text-muted italic mt-2 font-medium">
                      {LEVEL_DESCRIPTIONS[currentLevel]}
                    </p>
                  </div>

                  {/* Opção B: Redirecionamento para o teste rápido de 1 minuto */}
                  <button
                    onClick={() => {
                      localStorage.setItem('libritui_onboarding_target_lang', targetLang);
                      navigate(`/personal-hub/level-test?targetLang=${targetLang}`);
                    }}
                    className="w-full p-6 rounded-3xl border border-ok/20 hover:border-ok bg-ok/5 hover:bg-ok/10 text-left transition-all flex items-center justify-between group cursor-pointer"
                  >
                    <div className="space-y-1">
                      <span className="text-xs font-normal uppercase font-mono tracking-[0.14em] text-ok">
                        Não tem certeza?
                      </span>
                      <h3 className="font-semibold text-lg text-ink group-hover:text-ok transition-colors">
                        Fazer teste rápido de 1 minuto
                      </h3>
                      <p className="text-sm text-muted font-medium">
                        Responda o teste de vocabulário ativo para estimar seu nível exato.
                      </p>
                    </div>
                    <div className="w-10 h-10 rounded-full bg-ok text-surface flex items-center justify-center group-hover:scale-105 transition-transform">
                      <ChevronRight size={18} />
                    </div>
                  </button>
                </div>
              </div>
            )}

            {/* JANELA 3: JORNADA PARA A FLUÊNCIA */}
            {step === 3 && (
              <div className="space-y-6 text-center flex-1 flex flex-col justify-between py-4">
                <div className="space-y-6">
                  <div className="w-20 h-20 bg-tint-sage dark:bg-tint-sage text-ok rounded-3xl flex items-center justify-center mx-auto">
                    <Trophy size={44} className="animate-bounce" />
                  </div>

                  <div>
                    <span className="px-4 py-1.5 bg-ok/10 text-ok text-[13px] font-medium rounded-full border border-ok/20">
                      Jornada para a Fluência!
                    </span>
                    <h1 className="text-3xl font-medium text-ink leading-tight mt-4">
                      Tudo pronto para começar!
                    </h1>
                  </div>

                  <div className="p-6 rounded-3xl bg-tint-stone/40 border border-black/[0.06] dark:border-white/[0.06] max-w-md mx-auto space-y-3">
                    <p className="text-base md:text-lg font-bold text-ink">
                      Você está no nível <span className="text-ok font-semibold text-xl">{currentLevel}</span> em <span className="text-primary font-semibold">{getTargetLangName()}</span>.
                    </p>
                    <p className="text-sm text-muted font-medium leading-relaxed">
                      Continue estudando para aprender ainda mais e acelerar sua fluência!
                    </p>
                  </div>
                </div>
              </div>
            )}
          </motion.div>
        </AnimatePresence>

        {/* Wizard Footer Controls */}
        <div className="flex items-center justify-between pt-6 border-t border-black/[0.06] dark:border-white/[0.06] z-10 mt-6">
          {step > 1 ? (
            <button
              onClick={handlePrevStep}
              className="flex items-center gap-2 px-4 py-3 rounded-2xl text-muted hover:text-ink font-normal text-xs uppercase font-mono tracking-[0.14em] transition-all"
            >
              <ChevronLeft size={16} />
              Voltar
            </button>
          ) : (
            <div />
          )}

          {step < 3 ? (
            <button
              onClick={handleNextStep}
              className="flex items-center gap-2 px-8 py-4 bg-primary hover:bg-primary-dark text-slate-900 rounded-2xl font-medium text-[13px] hover:scale-[1.02] active:scale-95 transition-all cursor-pointer"
            >
              Continuar
              <ChevronRight size={16} />
            </button>
          ) : (
            <button
              onClick={handleFinishOnboarding}
              className="flex items-center gap-2 px-8 py-4 bg-ok hover:bg-ok text-surface rounded-2xl font-medium text-[13px] hover:scale-[1.02] active:scale-95 transition-all cursor-pointer"
            >
              Começar Jornada!
              <ChevronRight size={16} />
            </button>
          )}
        </div>
      </main>

    </div>
  );
}
